import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Building2 } from 'lucide-react';
import { NodeEntity } from '@/types/node.types';

const editFCSchema = z.object({
  code: z.string().min(1, 'الكود مطلوب').max(20, 'الكود يجب أن يكون أقل من 20 حرف'),
  name: z.string().min(2, 'الاسم يجب أن يكون حرفين على الأقل').max(100, 'الاسم يجب أن يكون أقل من 100 حرف'),
  city_id: z.string().min(1, 'المدينة مطلوبة'),
  neighborhood_id: z.string().min(1, 'الحي مطلوب'),
  address: z.string().min(5, 'العنوان يجب أن يكون 5 أحرف على الأقل'),
  phone: z
    .string()
    .optional()
    .refine((val) => !val || /^\+?[0-9]{9,15}$/.test(val), {
      message: 'صيغة رقم الهاتف غير صحيحة',
    }),
  email: z
    .string()
    .optional()
    .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), {
      message: 'صيغة البريد الإلكتروني غير صحيحة',
    }),
  is_active: z.boolean(),
});

type EditFCFormValues = z.infer<typeof editFCSchema>;

interface City {
  id?: string;
  city_id?: string;
  name?: string | { en: string; ar: string };
  name_ar?: string;
}

interface Neighborhood {
  id: string;
  name?: string;
  name_ar?: string;
}

interface EditFCModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: NodeEntity;
  onSuccess: () => void;
}

export function EditFCModal({ open, onOpenChange, node, onSuccess }: EditFCModalProps) {
  const { patch, get, loading } = useApi();
  const [cities, setCities] = useState<City[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
  const [loadingNeighborhoods, setLoadingNeighborhoods] = useState(false);

  const form = useForm<EditFCFormValues>({
    resolver: zodResolver(editFCSchema),
    defaultValues: {
      code: node.code,
      name: node.name,
      city_id: node.city_id,
      neighborhood_id: (node as any).neighborhood_id || '',
      address: node.address || '',
      phone: node.phone || '',
      email: node.email || '',
      is_active: node.is_active,
    },
  });

  const selectedCityId = form.watch('city_id');

  useEffect(() => {
    if (open) {
      fetchCities();
      form.reset({
        code: node.code,
        name: node.name,
        city_id: node.city_id,
        neighborhood_id: (node as any).neighborhood_id || '',
        address: node.address || '',
        phone: node.phone || '',
        email: node.email || '',
        is_active: node.is_active,
      });
    }
  }, [open, node]);

  useEffect(() => {
    if (!selectedCityId) {
      setNeighborhoods([]);
      return;
    }
    fetchNeighborhoods(selectedCityId);
    if (selectedCityId !== node.city_id) {
      form.setValue('neighborhood_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCityId]);

  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response: any = await get('/api/v1/settings/cities?lang=ar');
      if (response?.data && Array.isArray(response.data)) {
        setCities(response.data);
      } else if (response?.list && Array.isArray(response.list)) {
        setCities(response.list);
      } else if (Array.isArray(response)) {
        setCities(response);
      }
    } catch (error) {
      toast.error('فشل في جلب المدن');
    } finally {
      setLoadingCities(false);
    }
  };

  const fetchNeighborhoods = async (cityId: string) => {
    setLoadingNeighborhoods(true);
    try {
      const response: any = await get(`/api/v1/zones?city_id=${cityId}&lang=ar`);
      const list = Array.isArray(response) ? response : (response?.data ?? []);
      setNeighborhoods(list);
    } catch (error) {
      toast.error('فشل في جلب الأحياء');
      setNeighborhoods([]);
    } finally {
      setLoadingNeighborhoods(false);
    }
  };

  const onSubmit = async (values: EditFCFormValues) => {
    try {
      await patch(`/api/v1/dashboard/nodes/${node.id}`, {
        code: values.code.trim().toUpperCase(),
        name: values.name.trim(),
        city_id: values.city_id,
        neighborhood_id: values.neighborhood_id,
        address: values.address.trim(),
        phone: values.phone?.trim() || null,
        email: values.email?.trim()?.toLowerCase() || null,
        is_active: values.is_active,
      });
      toast.success('تم تحديث مركز الفرز بنجاح');
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'فشل في تحديث مركز الفرز';
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <Building2 className="h-5 w-5" />
            تعديل مركز الفرز
          </DialogTitle>
          <DialogDescription>
            قم بتعديل بيانات مركز الفرز
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                المعلومات الأساسية
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>كود مركز الفرز *</FormLabel>
                      <FormControl>
                        <Input placeholder="مثال: FC001" className="uppercase" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>اسم مركز الفرز *</FormLabel>
                      <FormControl>
                        <Input placeholder="أدخل اسم مركز الفرز" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="city_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>المدينة *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={loading || loadingCities}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={loadingCities ? 'جاري التحميل...' : 'اختر المدينة'} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {cities.map((city) => {
                          const cityId = city.id || city.city_id;
                          if (!cityId) return null;
                          const label =
                            city.name_ar ||
                            (typeof city.name === 'string'
                              ? city.name
                              : city.name?.ar || city.name?.en) ||
                            '';
                          return (
                            <SelectItem key={cityId} value={cityId}>
                              {label}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="neighborhood_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>الحي *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={loading || loadingNeighborhoods || !selectedCityId}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              !selectedCityId
                                ? 'اختر المدينة أولاً'
                                : loadingNeighborhoods
                                ? 'جاري التحميل...'
                                : neighborhoods.length === 0
                                ? 'لا توجد أحياء لهذه المدينة'
                                : 'اختر الحي'
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {neighborhoods.map((nb) => (
                          <SelectItem key={nb.id} value={nb.id}>
                            {nb.name_ar || nb.name || nb.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>العنوان *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="أدخل العنوان الكامل لمركز الفرز"
                        className="resize-none"
                        rows={2}
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                معلومات الاتصال
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>رقم الهاتف</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>البريد الإلكتروني</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="fc@company.com"
                          dir="ltr"
                          className="text-start"
                          {...field}
                          disabled={loading}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground border-b pb-2">
                الحالة
              </h3>

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>نشط</FormLabel>
                      <FormDescription className="text-xs">
                        مراكز الفرز غير النشطة لا يمكن استخدامها في العمليات
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={loading}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                إلغاء
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="ms-2 h-4 w-4 animate-spin" />}
                حفظ التعديلات
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
